import React, { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { Box, Text } from '@machobear/design-system'
import posthog from 'posthog-js'

import ENV_VARS from '@/config'
import { breakpoints } from '@/styles'
import useMediaQuery from '@/hooks/use-media-query'

declare global {
  interface Window {
    adsbygoogle: unknown[]
  }
}

const { BUILD_ENV } = ENV_VARS

const SLOT_ID_MAP: Record<string, string> = {
  'ad-7': '9858422494',
  'ad-15': '1523231931',
  'ad-23': '3455476306',
  'ad-31': '1908081604',
  'ad-39': '5733939685',
  'ad-47': '7960647779',
  fallback: '9100175912'
}
const SLOT_ID_MAP_TWO_COLUMN: Record<string, string> = {
  'ad-6': '9858422494',
  'ad-13': '1523231931',
  'ad-20': '3455476306',
  'ad-27': '1908081604',
  'ad-34': '5733939685',
  'ad-41': '7960647779',
  fallback: '9100175912'
}
const SLOT_POSITION_MAP: Record<string, string> = {
  'ad-7': '1',
  'ad-15': '2',
  'ad-23': '3',
  'ad-31': '4',
  'ad-39': '5',
  'ad-47': '6',
  fallback: 'fallback'
}
const SLOT_POSITION_MAP_TWO_COLUMN: Record<string, string> = {
  'ad-6': '1',
  'ad-13': '2',
  'ad-20': '3',
  'ad-27': '4',
  'ad-34': '5',
  'ad-41': '6',
  fallback: 'fallback'
}

const AD_INTERVAL = 8
const AD_INTERVAL_TWO_COLUMN = 7

export const useResponsiveAdValues = () => {
  const above1Column = useMediaQuery(`(min-width: ${breakpoints.map})`)
  const below3Column = useMediaQuery(`(max-width: ${breakpoints.mapLarge})`)
  const isTwoColumnLayout = above1Column && below3Column
  const adInterval = isTwoColumnLayout ? AD_INTERVAL_TWO_COLUMN : AD_INTERVAL
  const idMap = isTwoColumnLayout ? SLOT_ID_MAP_TWO_COLUMN : SLOT_ID_MAP
  const positionMap = isTwoColumnLayout
    ? SLOT_POSITION_MAP_TWO_COLUMN
    : SLOT_POSITION_MAP

  return { adInterval, isTwoColumnLayout, idMap, positionMap }
}

export const injectAds = (buildings: any, adInterval: number) => {
  return buildings.flatMap((building: any, index: number) => {
    const items = [building]
    if ((index + 1) % adInterval === 0) {
      items.push({ __typename: 'AdSense', id: `ad-${index}` })
    }
    return items
  })
}

// Hook to track when ads enter the viewport: This is important to avoid
// repeatedly loading ads off-screen which could violate Google AdSense
// policies for artificially inflating impressions, especially on mobile where
// the hidden listings list is frequently updated while interacting with map view.
const useAdVisibility = () => {
  const [isVisible, setIsVisible] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const currentRef = ref.current
    if (!currentRef) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isIntersecting = entry.isIntersecting
        setIsVisible(isIntersecting)

        if (isIntersecting && !hasLoaded) {
          setHasLoaded(true)
        }
      },
      {
        rootMargin: '600px 0px',
        threshold: 0.1
      }
    )

    observer.observe(currentRef)

    return () => {
      observer.unobserve(currentRef)
    }
  }, [hasLoaded])

  return { ref, isVisible, hasLoaded }
}

const GoogleAdSenseCardWrapper = ({
  children
}: {
  children: React.ReactNode
}) => {
  const { t } = useTranslation()

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap="gutter"
      background="customLight"
      style={{
        width: '100%',
        height: '395px'
      }}
    >
      <Text size="xsmall" tone="secondary">
        {t('Advertisement')}
      </Text>
      {children}
    </Box>
  )
}

const DevAdSenseCard = ({ slotPosition }: { slotPosition: string }) => {
  const router = useRouter()
  const { ref, isVisible, hasLoaded } = useAdVisibility()

  useEffect(() => {
    if (hasLoaded) {
      posthog.capture('map_list_ad_card_loaded')
      // console.log(
      //   '(dev) Ad slot',
      //   slotPosition,
      //   'loaded for path:',
      //   router.asPath
      // )
    }
  }, [hasLoaded, router.asPath, slotPosition])

  const randomColor = React.useMemo(() => {
    const hue = Math.random() * 360
    const saturation = 35 + Math.random() * 45 // 35-80%
    const lightness = 80 + Math.random() * 15 // 80-95%
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`
  }, [])

  return (
    <GoogleAdSenseCardWrapper>
      <Box
        ref={ref}
        id={`ad-slot-${slotPosition}`}
        border="borderField"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap="xsmall"
        style={{
          width: '300px',
          height: '250px',
          backgroundColor: randomColor
        }}
      >
        <Text size="xsmall" tone="secondary" weight="medium">
          Google AdSense - {slotPosition}
        </Text>
        <Text size="xsmall" tone="secondary">
          {isVisible ? '✓ Is visible' : '○ Is visible'}
        </Text>
        <Text size="xsmall" tone="secondary">
          {hasLoaded ? '✓ Has loaded' : '○ Has loaded'}
        </Text>
      </Box>
    </GoogleAdSenseCardWrapper>
  )
}

const AdSenseCard = ({
  slotId,
  slotPosition
}: {
  slotId: string
  slotPosition: string
}) => {
  const router = useRouter()
  const { ref, hasLoaded } = useAdVisibility()

  useEffect(() => {
    if (hasLoaded) {
      const intervalId = setInterval(() => {
        try {
          if (window.adsbygoogle) {
            window.adsbygoogle.push({})
            posthog.capture('map_list_ad_card_loaded')
            clearInterval(intervalId)
          }
        } catch (err) {
          // Pass
          clearInterval(intervalId)
        }
      }, 100)

      return () => clearInterval(intervalId)
    }
  }, [hasLoaded, router.asPath, slotId, slotPosition])

  return (
    <GoogleAdSenseCardWrapper>
      <div ref={ref}>
        <ins
          className="adsbygoogle"
          style={{
            display: 'inline-block',
            width: '300px',
            height: '250px'
            // border: '1px solid rgba(120, 120, 120, 1)',
            // background: 'rgba(120, 120, 120, 0.1)'
          }}
          data-ad-client="ca-pub-8869218122189233"
          data-ad-slot={slotId}
          // data-adtest="on"
        />
      </div>
    </GoogleAdSenseCardWrapper>
  )
}

const GoogleAdSenseCard = ({ positionId }: { positionId: string }) => {
  const router = useRouter()
  const { idMap, positionMap } = useResponsiveAdValues()
  const isDev = BUILD_ENV !== 'prod'

  const slotId = idMap[positionId] || idMap.fallback
  const slotPosition = positionMap[positionId] || positionMap.fallback

  // Unique key that changes when the URL is updated so ads get remounted
  const uniqueKey = `${positionId}-${router.asPath}`

  if (isDev) {
    return <DevAdSenseCard key={uniqueKey} slotPosition={slotPosition} />
  }

  return (
    <AdSenseCard key={uniqueKey} slotId={slotId} slotPosition={slotPosition} />
  )
}

export default GoogleAdSenseCard
